/* eslint-disable react/no-unescaped-entities */
import React, { useEffect, useState } from 'react'
import Menu from '@mui/material/Menu'
import MenuItem from '@mui/material/MenuItem'
import { Typography, useTheme } from '@mui/material'
import MoreVertIcon from '@mui/icons-material/MoreVert'
import { useNavigate } from 'react-router-dom'
import CustomModal from '../../../components/CustomModal'
import { StyledSaveMenuWrapper } from './styles'
import ROUTES from '../../../consts/paths'
import { useAppDispatch } from '../../../store'
import GenericButton from '../../../components/GenericButton'
import { ButtonSize, ButtonType } from '../../../components/GenericButton/styles'
import {
  getActionLoading,
  getEditableQuestion,
  getEditableSurvey,
  getSurveySections,
  getSurveyTitle
} from '../../../store/slices/SurveyTemplateSlice/selectors'
import { Messages, type Question, SurveyItemQuestionType } from '../types'
import { type ISurveySection, resetState } from '../../../store/slices/SurveyTemplateSlice'
import { createSurveyTemplate, editSurveyTemplate, editSurveyTemplateConfiramtion } from '../../../store/slices/SurveyTemplateSlice/apis'
import { notification } from '../../../components/Notifications'
import { findInvalidQuestion, findInvalidSection, scrollToElement } from '../helpers'
import _ from 'lodash'
import { type ISurveySection as ITemplateSurveySection, type ISurveyItem } from '../../../models/SurveyTemplate'
import GenericModal from '../../../components/GenericModal'
import { transformSections, validateDataFormatForSections } from '../../SurveyFeature/components/SurveyFeatureStepper/utils'

const SurveyHeader: React.FC = (): JSX.Element => {
  const [anchorEl, setAnchorEl] = React.useState<null | HTMLElement>(null)
  const open = Boolean(anchorEl)
  const [updateConfirmationModal, setUpdateConfirmationModal] = useState<boolean>(false)
  const [isDeleteModalOpen, setDeleteModalOpen] = React.useState<boolean>(false)
  const theme = useTheme()
  const dispatch = useAppDispatch()
  const navigate = useNavigate()
  const surveyTitle = getSurveyTitle()
  const surveySections = getSurveySections()
  const editableQuestion = getEditableQuestion()
  const editableSurvey = getEditableSurvey()

  const isLoading = getActionLoading()

  const handleBack = (): void => {
    navigate(ROUTES.SURVEYS)
    dispatch(resetState())
  }

  const checkIsTitleDirty = (): boolean => {
    if (surveyTitle !== editableSurvey.title) return true
    return false
  }

  const checkIsSectionDirty = (section: ISurveySection, sectionIndex: number): boolean => {
    const areAllSectionEqual = _.isEqual(editableSurvey?.surveySections?.[sectionIndex], section)
    return !areAllSectionEqual
  }

  const checkIsSectionIsOld = (section: ISurveySection): boolean => {
    const t = editableSurvey.surveySections.find((s: ITemplateSurveySection) => s.surveySectionId === section.surveySectionId)
    if (t?.surveySectionId) {
      return true
    }
    return false
  }

  const checkQuestionIsDirty = (sectionIndex: number, newQuestion: Question): boolean => {
    let isDirty = false
    editableSurvey?.surveySections?.[sectionIndex]?.surveyItems.forEach((question: ISurveyItem) => {
      if (question.itemId === newQuestion.itemId) {
        const isQuestionEqual = _.isEqual(question, newQuestion)
        isDirty = !isQuestionEqual
      }
    })
    return isDirty
  }

  const submitEditableSurvey = async (): Promise<void> => {
    const isTitleDirty = checkIsTitleDirty()
    const areAllSectionsEqual = _.isEqual(surveySections, editableSurvey?.surveySections)
    const isTemplateDirty = isTitleDirty || !areAllSectionsEqual

    const preparedData = {
      title: surveyTitle,
      isDirty: isTemplateDirty,
      surveySections: surveySections?.map((section: ISurveySection, sectionIndex: number) => ({
        isDirty: checkIsSectionIsOld(section) ? checkIsSectionDirty(section, sectionIndex) : undefined,
        name: section.name,
        isRepeatPerInstructor: section.isRepeatPerInstructor,
        title: section.title ?? undefined,
        descr: section.descr,
        surveySectionId: section.surveySectionId ?? undefined,
        attributes: section.attributes,
        branchingFilters: section.branchingFilters,
        indentation: section.indentation,
        parentSurveySectionId: section.parentSurveySectionId,
        surveyItems: section.surveyItems?.map((question: Question) => ({
          isDirty: question.itemId && checkQuestionIsDirty(sectionIndex, question),
          itemId: question.itemId ?? undefined,
          itemType: question.itemType,
          prompt: question.prompt,
          isRequired: question.isRequired,
          branchingFilters: question?.branchingFilters,
          ...(question.itemType === SurveyItemQuestionType.MULTIPLE_CHOICE ||
            question.itemType === SurveyItemQuestionType.LINEAR_SCALE ||
            question.itemType === SurveyItemQuestionType.MULTI_SELECT
            ? { object: question.object }
            : {})
        }))
      }))
    }

    const surveyTemplateData = {
      urlId: editableSurvey.templateId,
      data: preparedData
    }

    try {
      const response = await dispatch(editSurveyTemplate(surveyTemplateData)).unwrap()
      if (response?.surveyProjectTemplateChange) {
        setUpdateConfirmationModal(true)
      }
    } catch (error) {
      console.error('Error editing survey template:', error)
    }
  }

  const submitCreateSurvey = async (): Promise<any> => {
    try {
      const data = { title: surveyTitle, surveySections }
      const res = await dispatch(createSurveyTemplate({ data }))
      if (res.meta?.requestStatus === 'fulfilled') {
        dispatch(resetState())
        navigate(`${ROUTES.SURVEY_LIST}/${res.payload?.templateId}`)
      }
    } catch (error) {
      console.error('Error creating survey template:', error)
    }
  }

  const handleSave = async (): Promise<void> => {
    if (!surveySections) return

    const invalidSection = findInvalidSection(surveySections)
    if (invalidSection) {
      notification(Messages.NO_QUESTION, { variant: 'error' })
      scrollToElement(invalidSection)
      return
    }

    const invalidQuestion = findInvalidQuestion(surveySections)
    if (invalidQuestion) {
      notification('Unable to save Survey Template', {
        variant: 'error',
        subTitle: 'Please fill in all required fields and check for duplicate question options.'
      })
      scrollToElement(invalidQuestion)
      return
    }

    const validity = validateDataFormatForSections(transformSections(surveySections as any))
    const falseId = Object.entries(validity).filter(([key, value]) => !value).map(([key, value]) => key)
    if (falseId.length > 0) {
      scrollToElement(falseId[0], 'red')
      notification('There is an error in the highlighted section or its child elements', { variant: 'error' })
      return
    }
    const submitSurvey = editableSurvey ? submitEditableSurvey : submitCreateSurvey
    await submitSurvey()
  }

  const handleClick = (event: React.MouseEvent<HTMLButtonElement>): void => {
    setAnchorEl(event.currentTarget)
  }

  const handleClose = (): void => {
    setAnchorEl(null)
  }

  const handleDeleteClick = (): void => {
    setAnchorEl(null)
    setDeleteModalOpen(true)
  }

  const handleConfirmationUpdateTemplate = async (): Promise<any> => {
    try {
      const res = await dispatch(editSurveyTemplateConfiramtion({ urlId: editableSurvey.templateId }))
      if (res.meta?.requestStatus === 'fulfilled') {
        notification('Updated in all Published Survey Projects', { variant: 'success' })
      }
    } catch (error) {
      console.error('Error creating survey template:', error)
    }
  }

  useEffect(() => {
    findInvalidSection(surveySections)
  }, [surveySections])

  return (
    <React.Fragment>
      <StyledSaveMenuWrapper>
        <GenericButton
          buttonType={ButtonType.Primary}
          buttonSize={ButtonSize.Medium}
          disabled={editableQuestion !== null || surveyTitle === ''}
          loading={isLoading}
          label={editableSurvey ? 'Save' : 'Create'}
          onClick={() => {
            void handleSave()
          }}
          style={{
            flexShrink: 0,
            [theme.breakpoints.down('md')]: {
              width: '100%'
            }
          }}
        />
        <GenericButton
          buttonType={ButtonType.Secondary}
          buttonSize={ButtonSize.Medium}
          icon={<MoreVertIcon />}
          onClick={handleClick}
          style={{
            flexShrink: 0,
            [theme.breakpoints.down('md')]: {
              width: '100% !important'
            }
          }}
        />
      </StyledSaveMenuWrapper>
      <Menu anchorEl={anchorEl} open={open} onClose={handleClose}>
        <MenuItem onClick={handleDeleteClick}>Delete</MenuItem>
      </Menu>
      <CustomModal
        isModalOpen={isDeleteModalOpen}
        title={'Delete Survey?'}
        onClose={() => {
          setDeleteModalOpen(false)
        }}
        contentText={'Are you sure you want to delete this survey? You cannot undo this action.'}
        paperProps={{ width: theme.spacing(101), height: theme.spacing(70) }}
      ></CustomModal>
      <GenericModal
        isOpen={updateConfirmationModal}
        title={'Confirm: Template in Use'}
        onClose={() => { setUpdateConfirmationModal(false) }}
        maxWidth='sm'
        onConfirm={handleConfirmationUpdateTemplate}
      >
        <Typography> This template is used in a published Survey Project. Changes won’t be reflected in these published Survey Projects until you confirm. </Typography>
      </GenericModal>

    </React.Fragment>
  )
}
export default SurveyHeader
